import { Flex, Spinner, Stack, Text } from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  type ClimateWeightingGroup,
  type CyclePhase,
  ProcurementOverviewFigure,
  useGetProcurementOverviewQuery,
  type ProcurementFigureDto,
  type ProcurementOverviewDto,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { ListLoadError } from "../../common/ListLoadError";
import { useApiError } from "../../common/errors/useApiError";
import { formatNumber } from "../../common/formatting/formatNumber";
import { ClimateWeighting, ClimateWeightingProcurements, parseClimateWeightingGroup } from "./ClimateWeighting";
import { CycleTimePerPhase, PhaseProcurements, parseCyclePhase } from "./CycleTimePerPhase";
import { OverviewBar } from "./OverviewBar";
import { OverviewFigureGrid, OverviewFigureTile } from "./OverviewFigureTile";
import { IncompletePeriod, OverviewPeriodChoice, useOverviewPeriod } from "./OverviewPeriodChoice";
import { ProcurementWorkload } from "./ProcurementWorkload";
import {
  FIGURE_ORDER,
  procurementFigureDefinition,
  procurementFigureLabel,
  procurementListLink,
  procurementScopeText,
} from "./procurementFigures";
import { regulationLabel } from "../../common/procurement/regulationLabel";

export const ProcurementsOverview = () => {
  const organizationId = useLoggedInWithOrgContextState().selectedOrg.id;
  const periodState = useOverviewPeriod();
  const { range } = periodState;
  const [searchParams] = useSearchParams();
  const phase = parseCyclePhase(searchParams.get("phase"));
  const climateGroup = parseClimateWeightingGroup(searchParams.get("climate"));
  const { data, isLoading, isFetching, error, refetch } = useGetProcurementOverviewQuery(
    range ? { orgId: organizationId, from: range.from, to: range.to } : skipToken,
  );
  const { message } = useApiError();

  const linkWith = (key: "phase" | "climate", next: string | null) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set(key, next);
    else params.delete(key);
    return `?${params}`;
  };
  const linkWithPhase = (next: CyclePhase | null) => linkWith("phase", next);
  const linkWithClimateGroup = (next: ClimateWeightingGroup | null) => linkWith("climate", next);

  return (
    <Stack spacing="5">
      <Flex gap="3" wrap="wrap" align="center">
        <OverviewPeriodChoice state={periodState} />
        {isFetching && !isLoading && <Spinner size="sm" color="smPrimary" />}
      </Flex>
      {!range ? (
        <IncompletePeriod />
      ) : isLoading ? (
        <Spinner color="smPrimary" />
      ) : error || !data ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : (
        <>
          <Figures overview={data} />
          <CycleTimePerPhase overview={data} phaseLink={linkWithPhase} />
          {phase && <PhaseProcurements phase={phase} range={range} closeLink={linkWithPhase(null)} />}
          <ProcedureBars overview={data} />
          <RegulationBars overview={data} />
          {data.climateWeighting && (
            <ClimateWeighting climate={data.climateWeighting} groupLink={linkWithClimateGroup} />
          )}
          {data.climateWeighting && climateGroup && (
            <ClimateWeightingProcurements
              group={climateGroup}
              range={range}
              closeLink={linkWithClimateGroup(null)}
            />
          )}
        </>
      )}
      <ProcurementWorkload />
    </Stack>
  );
};

const Figures = ({ overview }: { overview: ProcurementOverviewDto }) => {
  const { t } = useTranslation();
  const byFigure = new Map(overview.figures.map((figure) => [figure.figure, figure]));
  const median = byFigure.get(ProcurementOverviewFigure.MedianCycleDays);
  const awaitingStandstill = byFigure.get(ProcurementOverviewFigure.Awarded)?.leftOut ?? 0;
  const withoutBids = byFigure.get(ProcurementOverviewFigure.AverageBids)?.leftOut ?? 0;
  return (
    <Stack spacing="4">
      <OverviewFigureGrid columns={{ base: 1, sm: 2, lg: 3 }}>
        {FIGURE_ORDER.map((key) => {
          const figure = byFigure.get(key);
          return figure ? (
            <OverviewFigureTile
              key={key}
              label={procurementFigureLabel(t, key)}
              shown={shownValue(t, figure)}
              definition={procurementFigureDefinition(t, key)}
              details={<Text>{figureDetails(t, figure, overview.deliveredBids)}</Text>}
              link={procurementListLink(figure.filter, key)}
              testId={key}
            />
          ) : null;
        })}
      </OverviewFigureGrid>
      <Stack spacing="1" fontSize="sm" color="smMuted">
        <Text>{t("Based on what you have access to: {{scope}}", { scope: procurementScopeText(t, overview.scope) })}</Text>
        {awaitingStandstill > 0 && (
          <Text>
            {t("{{count}} award decisions made in the period are not counted yet, because the award letters have not been sent or the standstill period is not over.", {
              count: awaitingStandstill,
            })}
          </Text>
        )}
        {median && median.leftOut > 0 && (
          <Text>
            {t("{{count}} awards without a publication date are not included in «Median cycle time».", {
              count: median.leftOut,
            })}
          </Text>
        )}
        {withoutBids > 0 && (
          <Text>
            {t("{{count}} competitions without bids are not included in «Average bids per competition» or «Competitions with one bid».", {
              count: withoutBids,
            })}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};

const shownValue = (t: TFunction, figure: ProcurementFigureDto): string => {
  const { value } = figure;
  if (value == null) return "–";
  switch (figure.figure) {
    case ProcurementOverviewFigure.MedianCycleDays:
      return t("{{count}} days", { count: Math.round(value) });
    case ProcurementOverviewFigure.AverageBids:
      return formatNumber(Math.round(value * 10) / 10);
    case ProcurementOverviewFigure.OneBidShare:
      return `${formatNumber(Math.round(value * 1000) / 10)} %`;
    default:
      return formatNumber(value);
  }
};

const figureDetails = (t: TFunction, figure: ProcurementFigureDto, deliveredBids: number): string => {
  switch (figure.figure) {
    case ProcurementOverviewFigure.MedianCycleDays:
      return figure.value == null
        ? t("No procurement awarded in the period has a publication date.")
        : t("Based on {{count}} awarded procurements.", { count: figure.procurements });
    case ProcurementOverviewFigure.AverageBids:
      return figure.value == null
        ? t("No competition with a bid deadline in the period got a bid.")
        : t("{{bids}} bids over {{count}} competitions.", { bids: formatNumber(deliveredBids), count: figure.procurements });
    case ProcurementOverviewFigure.OneBidShare:
      return t("{{count}} competitions with one bid.", { count: figure.procurements });
    case ProcurementOverviewFigure.NoBids:
      return t("{{count}} competitions", { count: figure.procurements });
    default:
      return t("{{count}} procurements", { count: figure.procurements });
  }
};

const ProcedureBars = ({ overview }: { overview: ProcurementOverviewDto }) => {
  const { t } = useTranslation();
  const total = overview.byProcedure.reduce((sum, bar) => sum + bar.procurements, 0);
  return (
    <BarCard
      title={t("Published per procedure")}
      empty={t("No public procurement with a procedure was published in this period.")}
      bars={overview.byProcedure.map((bar) => ({
        key: bar.procedureType,
        label: t(bar.procedureType),
        procurements: bar.procurements,
        link: procurementListLink(bar.filter, "byProcedure"),
      }))}
      total={total}
      testIdPrefix="procedure"
      note={
        overview.withoutProcedure > 0
          ? String(t("{{count}} procurements published in the period have no procedure, such as private competitions and call-offs, and are not in these bars.", {
              count: overview.withoutProcedure,
            }))
          : undefined
      }
    />
  );
};

const RegulationBars = ({ overview }: { overview: ProcurementOverviewDto }) => {
  const { t } = useTranslation();
  const total = overview.byRegulation.reduce((sum, bar) => sum + bar.procurements, 0);
  return (
    <BarCard
      title={t("Published per regulation")}
      empty={t("No procurement was published in this period.")}
      bars={overview.byRegulation.map((bar) => ({
        key: bar.bucket,
        label: regulationLabel(t, bar.bucket),
        procurements: bar.procurements,
        link: procurementListLink(bar.filter, "byRegulation"),
      }))}
      total={total}
      testIdPrefix="regulation"
    />
  );
};

const BarCard = ({
  title,
  empty,
  bars,
  total,
  testIdPrefix,
  note,
}: {
  title: string;
  empty: string;
  bars: { key: string; label: string; procurements: number; link: string }[];
  total: number;
  testIdPrefix: string;
  note?: string;
}) => {
  const { t } = useTranslation();
  const largest = Math.max(1, ...bars.map((bar) => bar.procurements));
  return (
    <Stack spacing="3" borderWidth="1px" borderColor="smBorder" borderRadius="lg" p="4" bg="smBackground">
      <Text as="h2" fontWeight="semibold" color="smText">
        {title}
      </Text>
      {bars.length === 0 ? (
        <Text fontSize="sm" color="smMuted">
          {empty}
        </Text>
      ) : (
        <Stack spacing="1">
          {bars.map((bar) => (
            <OverviewBar
              key={bar.key}
              label={bar.label}
              width={(bar.procurements / largest) * 100}
              shown={formatNumber(bar.procurements)}
              details={[
                t("{{count}} procurements published in the period", { count: bar.procurements }),
                t("{{share}} % of the total", { share: formatNumber(Math.round((bar.procurements / total) * 1000) / 10) }),
              ]}
              link={bar.link}
              testId={`${testIdPrefix}-${bar.key}`}
            />
          ))}
        </Stack>
      )}
      {note && (
        <Text fontSize="sm" color="smMuted">
          {note}
        </Text>
      )}
    </Stack>
  );
};
